// MakeAndModelForm.tsx
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { Loader2, AlertCircle, CheckCircle2, Package, Settings, Zap, Weight, Award, Factory, Plus } from "lucide-react";
import { MakeAndModel, Manufacturer } from "../../../types/manufacturer";
import { makeAndModelService, manufacturerService } from "../../../services/manufacturerService";
import ErrorAlert from "../../../shared/components/ui/ErrorAlert";
import Button from "../../../shared/components/ui/button/Button";
import Select from "../../../shared/components/form/Select";
import RightDrawer from "../../../shared/components/form/RightDrawer";

interface MakeAndModelFormProps {
  editingItem: MakeAndModel | null;
  onAdd: (item: MakeAndModel) => void;
  onClose: () => void;
  manufacturers: Manufacturer[];
  onManufacturerAdded?: (manufacturer: Manufacturer) => void;
}

export default function MakeAndModelForm({
  editingItem,
  onAdd,
  onClose,
  manufacturers,
  onManufacturerAdded
}: MakeAndModelFormProps) {
  const [apiError, setApiError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Manufacturer creation modal state
  const [showManufacturerModal, setShowManufacturerModal] = useState(false);
  const [isCreatingManufacturer, setIsCreatingManufacturer] = useState(false);
  const [manufacturerFormData, setManufacturerFormData] = useState({
    name: "",
    description: ""
  } as { name: string; description: string });

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty, isValid },
    setFocus
  } = useForm<any>({
    defaultValues: {
      name: "",
      code: "",
      manufacturerId: "",
      modelNumber: "",
      description: "",
      specifications: {
        power: "",
        voltage: "",
        current: "",
        frequency: "",
        efficiency: "",
        warrantyPeriod: "",
        certifications: []
      },
      status: "active"
    },
    mode: "onChange"
  });

  const watchedName = watch("name");
  const watchedDescription = (watch("description") as string) || "";
  const watchedManufacturerId = watch("manufacturerId");

  // Create new manufacturer
  const handleCreateManufacturer = async () => {
    if (!manufacturerFormData.name.trim()) {
      setApiError("Manufacturer name is required");
      return;
    }

    setIsCreatingManufacturer(true);
    setApiError(null);

    try {
      const payload: any = {
        name: manufacturerFormData.name.trim(),
        status: "active" as "active"
      };

      if (manufacturerFormData.description?.trim()) {
        payload.description = manufacturerFormData.description.trim();
      }

      const response = await manufacturerService.createManufacturer(payload);

      if (response.success && response.data) {
        // Update the manufacturer list
        onManufacturerAdded?.(response.data);

        // Set the new manufacturer as selected - convert to string to match dropdown format
        const newManufacturerId = String(response.data._id || response.data.id || '');
        console.log('Setting new manufacturer ID:', newManufacturerId, 'from data:', response.data);
        setValue("manufacturerId", newManufacturerId, { shouldValidate: true, shouldDirty: true });

        // Close modal and reset form
        setShowManufacturerModal(false);
        setManufacturerFormData({ name: "", description: "" });
        setApiError(null);

        // Show success message
        toast.success(`Manufacturer "${response.data.name}" created and selected!`);
      } else {
        throw new Error(response.message || "Failed to create manufacturer");
      }
    } catch (err: any) {
      setApiError(err.response?.data?.message || err.message || "Failed to create manufacturer");
      console.error("Manufacturer creation error:", err);
    } finally {
      setIsCreatingManufacturer(false);
    }
  };

  // Manufacturer options for dropdown
  const manufacturerOptions = [
    ...manufacturers.map(m => ({
      label: m.name,
      value: String(m._id || m.id || '')
    })),
    {
      label: "Add New Manufacturer...",
      value: "add_manufacturer",
      isAddOption: true
    }
  ];

  // Set form values when editing
  useEffect(() => {
    if (editingItem) {
      let mId = "";
      if (editingItem.manufacturer && (editingItem.manufacturer._id || editingItem.manufacturer.id)) {
        mId = String(editingItem.manufacturer._id || editingItem.manufacturer.id);
      } else if (typeof editingItem.manufacturerId === 'object' && editingItem.manufacturerId !== null) {
        mId = String((editingItem.manufacturerId as any)._id || (editingItem.manufacturerId as any).id || "");
      } else if (editingItem.manufacturerId) {
        mId = String(editingItem.manufacturerId);
      }

      reset({
        name: editingItem.name,
        code: editingItem.code || "",
        manufacturerId: mId,
        modelNumber: editingItem.modelNumber || "",
        description: editingItem.description || "",
        specifications: {
          power: editingItem.specifications?.power || "",
          voltage: editingItem.specifications?.voltage || "",
          current: (editingItem.specifications as any)?.current || "",
          frequency: editingItem.specifications?.frequency || "",
          efficiency: editingItem.specifications?.efficiency || "",
          warrantyPeriod: editingItem.specifications?.warrantyPeriod || "",
          certifications: editingItem.specifications?.certifications || []
        },
        status: editingItem.status
      });
    } else {
      reset();
    }
    setFocus("name");
  }, [editingItem, reset, setFocus]);

  const onSubmit = async (data: any) => {
    setIsSubmitting(true);
    setApiError(null);
    setShowSuccess(false);

    try {
      let res;
      const payload: any = {
        name: data.name.trim(),
        manufacturerId: data.manufacturerId,
        status: "active"
      };

      // Only include optional fields if they have values
      if (data.code?.trim()) {
        payload.code = data.code.trim().toUpperCase();
      }
      if (data.modelNumber?.trim()) {
        payload.modelNumber = data.modelNumber.trim();
      }
      if (data.description?.trim()) {
        payload.description = data.description.trim();
      }

      // Specifications - only include if any field has value
      const specifications: any = {};
      if (data.specifications?.power) {
        specifications.power = parseFloat(data.specifications.power);
      }
      if (data.specifications?.voltage) {
        specifications.voltage = parseFloat(data.specifications.voltage);
      }
      if (data.specifications?.current) {
        specifications.current = parseFloat(data.specifications.current);
      }
      if (data.specifications?.frequency) {
        specifications.frequency = parseFloat(data.specifications.frequency);
      }
      if (data.specifications?.efficiency) {
        specifications.efficiency = parseFloat(data.specifications.efficiency);
      }
      if (data.specifications?.warrantyPeriod) {
        specifications.warrantyPeriod = parseInt(data.specifications.warrantyPeriod);
      }
      if (data.specifications?.certifications && data.specifications.certifications.length > 0) {
        specifications.certifications = data.specifications.certifications.filter((cert: string) => cert.trim());
      }

      if (Object.keys(specifications).length > 0) {
        payload.specifications = specifications;
      }

      if (editingItem) {
        const makeAndModelId = editingItem._id || editingItem.id;
        if (!makeAndModelId) {
          throw new Error("Make and Model ID is required for update");
        }
        res = await makeAndModelService.updateMakeAndModel(String(makeAndModelId), payload);
      } else {
        res = await makeAndModelService.createMakeAndModel(payload);
      }

      setShowSuccess(true);
      setTimeout(() => {
        onAdd(res.data);
        onClose();
      }, 1000);
    } catch (err: any) {
      setApiError(err.response?.data?.message || "An error occurred while saving the make & model");
      console.error("Form submission error:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {showSuccess && (
        <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
          <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-green-800">
              {editingItem ? "Make & Model updated successfully!" : "Make & Model created successfully!"}
            </p>
          </div>
        </div>
      )}

      {apiError && (
        <ErrorAlert
          message={apiError}
          onClose={() => setApiError(null)}
          className="mb-4"
        />
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        {/* Basic Information Section */}
        <div className="bg-gray-50 rounded-xl p-6 space-y-6">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Package className="w-5 h-5" />
            Basic Information
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Name Field */}
            <div className="space-y-2">
              <label
                htmlFor="name"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Make & Model Name *
              </label>
              <div className="relative">
                <input
                  id="name"
                  type="text"
                  {...register("name", {
                    required: "Make & Model name is required",
                    minLength: {
                      value: 2,
                      message: "Name must be at least 2 characters"
                    },
                    maxLength: {
                      value: 200,
                      message: "Name must be less than 200 characters"
                    },
                  })}
                  className={`h-12 w-full rounded-xl border bg-white px-4 py-3 text-base text-gray-800 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${errors.name
                    ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                    : watchedName && !errors.name
                      ? "border-green-300 focus:border-green-500 focus:ring-green-500/20"
                      : "border-gray-300 focus:border-brand-500 focus:ring-brand-500/20"
                    }`}
                  placeholder="Enter make & model name"
                />
                {watchedName && !errors.name && (
                  <CheckCircle2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-green-500" />
                )}
                {errors.name && (
                  <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500" />
                )}
              </div>
              {errors.name && (
                <p className="flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {errors.name.message as string}
                </p>
              )}
            </div>



            {/* Manufacturer Field */}
            <div className="space-y-2">
              <label
                htmlFor="manufacturerId"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Manufacturer *
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Select
                    options={manufacturerOptions}
                    value={manufacturerOptions.find((opt) => opt.value === watchedManufacturerId)}
                    placeholder="Select manufacturer"
                    onChange={(selectedOption) => {
                      if (selectedOption?.value === "add_manufacturer") {
                        setShowManufacturerModal(true);
                      } else {
                        setValue("manufacturerId", selectedOption?.value || "", { shouldValidate: true });
                      }
                    }}
                  />
                  {errors.manufacturerId && (
                    <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500" />
                  )}
                </div>
                {/* Add Manufacturer Button */}
                <button
                  type="button"
                  onClick={() => setShowManufacturerModal(true)}
                  className="h-12 px-4 flex-shrink-0 inline-flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-brand-300 bg-brand-50 text-brand-600 hover:border-brand-500 hover:bg-brand-100 hover:text-brand-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 group"
                  title="Add new manufacturer"
                >
                  <Plus className="w-5 h-5 group-hover:scale-110 transition-transform duration-200" />
                  <span className="text-sm font-medium hidden sm:inline">Add</span>
                </button>
              </div>
              {errors.manufacturerId && (
                <p className="flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {errors.manufacturerId.message as string}
                </p>
              )}
              {/* Hidden field for validation */}
              <input
                type="hidden"
                {...register("manufacturerId", { required: "Manufacturer is required" })}
              />
            </div>

            {/* Model Number Field */}
            <div className="space-y-2">
              <label
                htmlFor="modelNumber"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Model Number
                <span className="text-gray-400 font-normal ml-1">(optional)</span>
              </label>
              <input
                id="modelNumber"
                type="text"
                {...register("modelNumber", {
                  maxLength: {
                    value: 100,
                    message: "Model number must be less than 100 characters"
                  }
                })}
                className={`h-12 w-full rounded-xl border bg-white px-4 py-3 text-base text-gray-800 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${errors.modelNumber
                  ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                  : "border-gray-300 focus:border-brand-500 focus:ring-brand-500/20"
                  }`}
                placeholder="Enter model number"
              />
              {errors.modelNumber && (
                <p className="flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {errors.modelNumber.message as string}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Technical Specifications Section */}
        <div className="bg-gray-50 rounded-xl p-6 space-y-6">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Technical Specifications
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Power */}
            <div className="space-y-2">
              <label
                htmlFor="specifications.power"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                <Zap className="w-4 h-4 inline mr-1" />
                Power (W)
                <span className="text-gray-400 font-normal ml-1">(optional)</span>
              </label>
              <input
                id="specifications.power"
                type="number"
                min="0"
                step="0.01"
                {...register("specifications.power", {
                  min: {
                    value: 0,
                    message: "Power must be a positive number"
                  }
                })}
                className={`h-12 w-full rounded-xl border bg-white px-4 py-3 text-base text-gray-800 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${(errors as any).specifications?.power
                  ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                  : "border-gray-300 focus:border-brand-500 focus:ring-brand-500/20"
                  }`}
                placeholder="Enter power in watts"
              />
              {(errors as any).specifications?.power && (
                <p className="flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {(errors as any).specifications.power.message as string}
                </p>
              )}
            </div>

            {/* Current */}
            <div className="space-y-2">
              <label
                htmlFor="specifications.current"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                <Zap className="w-4 h-4 inline mr-1 text-amber-500" />
                Current (A)
                <span className="text-gray-400 font-normal ml-1">(optional)</span>
              </label>
              <input
                id="specifications.current"
                type="number"
                min="0"
                step="0.01"
                {...register("specifications.current", {
                  min: {
                    value: 0,
                    message: "Current must be a positive number"
                  }
                })}
                className={`h-12 w-full rounded-xl border bg-white px-4 py-3 text-base text-gray-800 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${(errors as any).specifications?.current
                  ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                  : "border-gray-300 focus:border-brand-500 focus:ring-brand-500/20"
                  }`}
                placeholder="Enter current in amperes"
              />
              {(errors as any).specifications?.current && (
                <p className="flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {(errors as any).specifications.current.message as string}
                </p>
              )}
            </div>

            {/* Voltage */}
            <div className="space-y-2">
              <label
                htmlFor="specifications.voltage"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                <Settings className="w-4 h-4 inline mr-1" />
                Voltage (V)
                <span className="text-gray-400 font-normal ml-1">(optional)</span>
              </label>
              <input
                id="specifications.voltage"
                type="number"
                min="0"
                step="0.1"
                {...register("specifications.voltage", {
                  min: {
                    value: 0,
                    message: "Voltage must be a positive number"
                  }
                })}
                className={`h-12 w-full rounded-xl border bg-white px-4 py-3 text-base text-gray-800 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${(errors.specifications as any)?.voltage
                  ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                  : "border-gray-300 focus:border-brand-500 focus:ring-brand-500/20"
                  }`}
                placeholder="Enter voltage"
              />
              {(errors.specifications as any)?.voltage && (
                <p className="flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {(errors.specifications as any).voltage.message as string}
                </p>
              )}
            </div>

            {/* Frequency */}
            <div className="space-y-2">
              <label
                htmlFor="specifications.frequency"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Frequency (Hz)
                <span className="text-gray-400 font-normal ml-1">(optional)</span>
              </label>
              <input
                id="specifications.frequency"
                type="number"
                min="0"
                step="0.1"
                {...register("specifications.frequency", {
                  min: {
                    value: 0,
                    message: "Frequency must be a positive number"
                  }
                })}
                className={`h-12 w-full rounded-xl border bg-white px-4 py-3 text-base text-gray-800 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${(errors.specifications as any)?.frequency
                  ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                  : "border-gray-300 focus:border-brand-500 focus:ring-brand-500/20"
                  }`}
                placeholder="Enter frequency"
              />
              {(errors.specifications as any)?.frequency && (
                <p className="flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {(errors.specifications as any).frequency.message as string}
                </p>
              )}
            </div>

            {/* Efficiency */}
            <div className="space-y-2">
              <label
                htmlFor="specifications.efficiency"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Efficiency (%)
                <span className="text-gray-400 font-normal ml-1">(optional)</span>
              </label>
              <input
                id="specifications.efficiency"
                type="number"
                min="0"
                max="100"
                step="0.01"
                {...register("specifications.efficiency", {
                  min: {
                    value: 0,
                    message: "Efficiency must be between 0 and 100"
                  },
                  max: {
                    value: 100,
                    message: "Efficiency must be between 0 and 100"
                  }
                })}
                className={`h-12 w-full rounded-xl border bg-white px-4 py-3 text-base text-gray-800 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${(errors.specifications as any)?.efficiency
                  ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                  : "border-gray-300 focus:border-brand-500 focus:ring-brand-500/20"
                  }`}
                placeholder="Enter efficiency percentage"
              />
              {(errors.specifications as any)?.efficiency && (
                <p className="flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {(errors.specifications as any).efficiency.message as string}
                </p>
              )}
            </div>

            {/* Warranty Period */}
            <div className="space-y-2">
              <label
                htmlFor="specifications.warrantyPeriod"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                <Award className="w-4 h-4 inline mr-1" />
                Warranty (months)
                <span className="text-gray-400 font-normal ml-1">(optional)</span>
              </label>
              <input
                id="specifications.warrantyPeriod"
                type="number"
                min="0"
                step="1"
                {...register("specifications.warrantyPeriod", {
                  min: {
                    value: 0,
                    message: "Warranty period must be a positive number"
                  }
                })}
                className={`h-12 w-full rounded-xl border bg-white px-4 py-3 text-base text-gray-800 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${(errors.specifications as any)?.warrantyPeriod
                  ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                  : "border-gray-300 focus:border-brand-500 focus:ring-brand-500/20"
                  }`}
                placeholder="Enter warranty period in months"
              />
              {(errors.specifications as any)?.warrantyPeriod && (
                <p className="flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {(errors.specifications as any).warrantyPeriod.message as string}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons - Sticky Footer */}
        <div className="sticky bottom-0 flex flex-col sm:flex-row justify-end gap-3 pt-5 pb-2 border-t border-gray-200 mt-6 bg-white z-10">
          <Button
            type="button"
            variant="outline"
            size="lg"
            onClick={onClose}
            disabled={isSubmitting}
            className="w-full sm:w-auto order-2 sm:order-1"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="sorigin"
            size="lg"
            disabled={isSubmitting || !isDirty || !isValid}
            className="w-full sm:w-auto min-w-[140px] order-1 sm:order-2"
          >
            {isSubmitting ? (
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                {editingItem ? "Updating..." : "Creating..."}
              </div>
            ) : editingItem ? (
              "Update Make & Model"
            ) : (
              "Create Make & Model"
            )}
          </Button>
        </div>
      </form>

      {/* Add Manufacturer Modal */}
      <RightDrawer
        open={showManufacturerModal}
        onClose={() => {
          setShowManufacturerModal(false);
          setManufacturerFormData({ name: "", description: "" });
          setApiError(null);
        }}
        title="Add New Manufacturer"
        size="md"
      >
        <div className="space-y-6">
          {apiError && (
            <ErrorAlert
              message={apiError}
              onClose={() => setApiError(null)}
              className="mb-4"
            />
          )}

          <div className="space-y-4">
            {/* Manufacturer Name */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Manufacturer Name *
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={manufacturerFormData.name}
                  onChange={(e) => setManufacturerFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="h-12 w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-base text-gray-800 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 focus:border-brand-500 focus:ring-brand-500/20 transition-all duration-200"
                  placeholder="Enter manufacturer name"
                />
                {manufacturerFormData.name && (
                  <CheckCircle2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-green-500" />
                )}
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Description
                <span className="text-gray-400 font-normal ml-1">(optional)</span>
              </label>
              <textarea
                value={manufacturerFormData.description}
                onChange={(e) => setManufacturerFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-base text-gray-800 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 focus:border-brand-500 focus:ring-brand-500/20 transition-all duration-200 resize-none"
                placeholder="Enter manufacturer description"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              size="lg"
              onClick={() => {
                setShowManufacturerModal(false);
                setManufacturerFormData({ name: "", description: "" });
                setApiError(null);
              }}
              disabled={isCreatingManufacturer}
              className="w-full sm:w-auto order-2 sm:order-1"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="sorigin"
              size="lg"
              onClick={handleCreateManufacturer}
              disabled={isCreatingManufacturer || !manufacturerFormData.name.trim()}
              className="w-full sm:w-auto min-w-[140px] order-1 sm:order-2"
            >
              {isCreatingManufacturer ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating...
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Factory size={16} />
                  Create Manufacturer
                </div>
              )}
            </Button>
          </div>
        </div>
      </RightDrawer>
    </div>
  );
}
